import * as React from 'react'
import Menu from '@mui/material/Menu'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined'
import { themeConfig } from '@gap/shared/theme'
import { observer } from 'mobx-react-lite'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined'
import useStore from 'reusable/mobx-store-utils'
import { Analytics, AnalyticsEvent } from 'analytics/analytics'
import { handleCircleSSO } from 'reusable/utils/sso'
import { useOrgGuardStore } from '../OrgGuardStore'
import variables from 'variables/variables'
import { canonicalStage } from '@gap/utils/stage'
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import WasThisHelpfull from 'reusable/was-this-helpfull/WasThisHelpfull'
import { ulid } from 'ulid'
import ModalState from 'reusable/mobx-helpers/ModalState'
import Divider from '@mui/material/Divider'
import { FeedbackTypeEnum } from '@gap/backend/app-services/feedback/models/FeedbackTypeEnum'

interface HelperMenuListItem {
   icon: React.ReactNode
   title: string
   url?: string
   onClick?: () => void
}

interface HelperMenuProps {
   extraListItems?: HelperMenuListItem[]
   feedbackType: FeedbackTypeEnum
   iconColor?: string
}

export default observer(function HelperMenu(props: HelperMenuProps) {
   const orgGuardStore = useOrgGuardStore()

   const helperMenuStore = useStore(sp => ({
      anchorEl: null as null | HTMLElement,
      feedbackModalState: new ModalState(),
      feedbackId: ulid(),
      get open() {
         return Boolean(helperMenuStore.anchorEl)
      },
      get askTheCommunityUrl() {
         return canonicalStage(variables.stage) === 'prod' ? 'https://learn.thegaphq.com/c/gap-chat' : 'https://gap-test-academy.circle.so/c/gap-chat'
      },
      handleClick: (event: React.MouseEvent<HTMLElement>) => {
         helperMenuStore.anchorEl = event.currentTarget
      },
      handleClose: () => {
         helperMenuStore.anchorEl = null
      },
      onHelpClick: () => {
         Analytics.emitEvent(AnalyticsEvent.helpArticlesViewed, {
            "Initiated from": 'Help menu',
         })
      },
      onCommunityClick: () => {
         Analytics.emitEvent(AnalyticsEvent.communityGroupViewed, {
            "Initiated from": 'Help menu',
         })
      },
      onGapAcademyClick: () => {
         handleCircleSSO(sp.orgGuardStore.userData, 'Help menu')
      },
      closeFeedbackModalState: () => {
         helperMenuStore.feedbackModalState.close()
         helperMenuStore.feedbackId = ulid() // Reset feedback id incase they want to submit another feedback
      }
   }), { orgGuardStore })

   return <>
      <IconButton aria-label="Help" onClick={helperMenuStore.handleClick}>
         <HelpOutlineOutlined sx={{ color: props.iconColor ?? themeConfig.primary['800'] }} />
      </IconButton>

      <GapDialog modalState={helperMenuStore.feedbackModalState}>
         <WasThisHelpfull type={props.feedbackType} entityId={helperMenuStore.feedbackId} skipToImproveDialog onClose={helperMenuStore.closeFeedbackModalState} />
      </GapDialog>

      <Menu
         anchorEl={helperMenuStore.anchorEl}
         id="helper-menu"
         open={helperMenuStore.open}
         onClose={helperMenuStore.handleClose}
         onClick={helperMenuStore.handleClose}
         slotProps={{
            paper: {
               elevation: 0,
               sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  width: '259px'
               },
            }
         }}
         transformOrigin={{ horizontal: 'right', vertical: 'top' }}
         anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
         {props.extraListItems?.map(item => (
            <HelperListItem key={item.title} icon={item.icon} title={item.title} url={item.url} onClick={item.onClick} />
         ))}
         {!!props.extraListItems?.length && <Divider />}
         <HelperListItem icon={<HelpOutlineOutlined />} title="Help centre" url="https://help.thegaphq.com/portal/en/kb" onClick={helperMenuStore.onHelpClick} />
         {orgGuardStore.showGapAcademy && <HelperListItem icon={<MenuBookOutlined />} title="Gap Academy" onClick={helperMenuStore.onGapAcademyClick} />}
         <HelperListItem icon={<GroupsOutlined />} title="Ask the community" url={helperMenuStore.askTheCommunityUrl} onClick={helperMenuStore.onCommunityClick} />
         <Divider />
         <HelperListItem icon={<EmailOutlined />} title="Contact support" url="https://help.thegaphq.com/portal/en/newticket" />
         <HelperListItem icon={<FeedbackOutlined />} title="Submit feedback" onClick={helperMenuStore.feedbackModalState.open} />
      </Menu>
   </>
})

const HelperListItem = observer(function HelperListItem(props: {
   icon: React.ReactNode,
   title: string,
   url?: string,
   onClick?: () => void
}) {
   return (
      <ListItemButton
         {...(props.url && {
            href: props.url,
            target: "_blank",
            rel: "noopener noreferrer",
         })}
         onClick={props.onClick}
         sx={{
            ':hover': {
               backgroundColor: themeConfig.primary['200'],
               textDecoration: 'none',
               color: 'inherit'
            },
         }}

      >
         <ListItemIcon sx={{ color: themeConfig.primary['800'] }}>{props.icon}</ListItemIcon>
         <ListItemText>
            <Typography variant="body2">{props.title}</Typography>
         </ListItemText>
      </ListItemButton>
   )
})
